import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DIRECTUS_INSTANCE } from '../../../api';
import { formatDisplayDateTime } from '../../../utils/dateFormatting';

const SourceDrawer = ({ isOpen, onClose, source, workspaceId }) => {
  const navigate = useNavigate();
  const [dataset, setDataset] = useState(null);
  const [loadingDataset, setLoadingDataset] = useState(false);

  useEffect(() => {
    if (!isOpen || !source?.filename || !workspaceId) {
      return;
    }

    const findDataset = async () => {
      setLoadingDataset(true);
      try {
        const token = localStorage.getItem('directus_token');
        if (!token) {
          setLoadingDataset(false);
          return;
        }

        const response = await axios.get(
          `${DIRECTUS_INSTANCE}/items/data_sets`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            params: {
              'filter[linked_workspace][_eq]': workspaceId,
              fields: '*'
            }
          }
        );

        if (response.data && response.data.data) {
          const matchingDataset = response.data.data.find((datasetEntry) => {
            const datasetName = datasetEntry.name || '';
            const sourceFilename = source.filename || '';

            if (datasetName === sourceFilename) return true;

            const baseDatasetName = datasetName.replace(/\.[^/.]+$/, '');
            const baseSourceName = sourceFilename.replace(/\.[^/.]+$/, '');

            return baseDatasetName === baseSourceName
              || datasetName.includes(baseSourceName)
              || baseSourceName.includes(baseDatasetName);
          });

          if (matchingDataset) {
            setDataset(matchingDataset);
          } else {
            setDataset(null);
          }
        }
      } catch (error) {
        console.error('Error finding dataset:', error);
        setDataset(null);
      } finally {
        setLoadingDataset(false);
      }
    };

    findDataset();
  }, [isOpen, source?.filename, workspaceId]);

  if (!isOpen) return null;

  if (!source) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Source Details</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <p>No source data available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Source Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl font-bold"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="font-medium text-gray-600">Filename:</span>
                <p className="text-gray-800">{source.filename || 'N/A'}</p>
              </div>
              <div>
                <span className="font-medium text-gray-600">File ID:</span>
                <p className="text-gray-800 font-mono text-sm">{source.file_id || 'N/A'}</p>
              </div>
              <div>
                <span className="font-medium text-gray-600">File Size:</span>
                <p className="text-gray-800">{source.bytes ? `${(source.bytes / 1024).toFixed(2)} KB` : 'N/A'}</p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Created:</span>
                <p className="text-gray-800">
                  {source.created_at
                    ? formatDisplayDateTime(source.created_at * 1000, localStorage.getItem('i18nextLng') || 'en')
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {source.purpose && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-3 text-blue-800">File Details</h3>
              <div className="space-y-2">
                <div>
                  <span className="font-medium text-blue-700">Purpose:</span>
                  <p className="text-blue-800">{source.purpose}</p>
                </div>
                {source.status && (
                  <div>
                    <span className="font-medium text-blue-700">Status:</span>
                    <span className={`ml-2 px-2 py-1 rounded text-sm font-medium ${
                      source.status === 'processed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}
                    >
                      {source.status}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {source.content && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-3 text-gray-700">Content Preview</h3>
              <div className="bg-white p-3 rounded border max-h-64 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
                  {source.content.substring(0, 1000)}
                  {source.content.length > 1000 && '...'}
                </pre>
              </div>
            </div>
          )}

          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-3 text-green-800">Actions</h3>
            <div className="space-y-3">
              {loadingDataset ? (
                <p className="text-green-700">Finding dataset...</p>
              ) : dataset ? (
                <button
                  onClick={() => navigate(`/datasets/details/${dataset.id}`)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  View Dataset: {dataset.name}
                </button>
              ) : (
                <button
                  onClick={() => navigate('/datasets')}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Dataset not found - Browse all
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SourceDrawer;
